import React, { useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, LinearProgress, Link, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography } from "@material-ui/core";
import ChevronDownIcon from "mdi-material-ui/ChevronDown";

const itemFields = gql`fragment InventoryBatchItem on InventoryDiagnosticBatchItem {
  itemId productId productNumber serialNumbers changes eligible status error
}`;
export const previewMutation = gql`mutation previewInventoryDiagnosticBatch($input: PreviewInventoryDiagnosticBatchInput!) {
  previewInventoryDiagnosticBatch(input: $input) { _id action selectedCount expiresAt items { ...InventoryBatchItem } }
} ${itemFields}`;
export const executeMutation = gql`mutation executeInventoryDiagnosticBatchItem($batchId: ID!, $itemId: ID!) {
  executeInventoryDiagnosticBatchItem(batchId: $batchId, itemId: $itemId) { ...InventoryBatchItem }
} ${itemFields}`;
export const batchQuery = gql`query inventoryDiagnosticBatch($batchId: ID!) {
  inventoryDiagnosticBatch(batchId: $batchId) { _id action selectedCount expiresAt items { ...InventoryBatchItem } }
} ${itemFields}`;

export function DiagnosticProductLink({ productId, productNumber, newTab = false }) {
  return productId ? <Link component={RouterLink} to={`/products/${productId}`}
    target={newTab ? "_blank" : undefined} rel={newTab ? "noopener noreferrer" : undefined}>
    {productNumber || "Produkt öffnen"}
  </Link> : <>{productNumber || "–"}</>;
}

export function useInventorySelection(key, nodes, idField) {
  const [selectedIds, setSelectedIds] = useState([]);
  useEffect(() => { setSelectedIds([]); }, [key]);
  const pageIds = nodes.map((row) => row[idField]).filter(Boolean);
  const pageKey = JSON.stringify(pageIds);
  useEffect(() => {
    const available = new Set(JSON.parse(pageKey));
    setSelectedIds((previous) => {
      const next = previous.filter((id) => available.has(id));
      return next.length === previous.length ? previous : next;
    });
  }, [pageKey]);
  const selected = new Set(selectedIds);
  const checked = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  return { selectedIds, checked, indeterminate: !checked && pageIds.some((id) => selected.has(id)),
    isSelected: (id) => selected.has(id),
    toggle: (id) => setSelectedIds((previous) => previous.includes(id) ? previous.filter((value) => value !== id) : [...previous, id]),
    toggleAll: () => setSelectedIds(checked ? [] : pageIds),
    clear: () => setSelectedIds([]) };
}

const STATUS = { pending: "Geplant", running: "Läuft", done: "Erledigt", failed: "Fehlgeschlagen", skipped: "Übersprungen" };
export default function InventoryBatchActions({ shopId, selection, runId, disabled, pageCount, onComplete }) {
  const client = useApolloClient();
  const [createPreview] = useMutation(previewMutation);
  const [execute] = useMutation(executeMutation);
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const stop = useRef(false);
  const request = useRef(0);
  useEffect(() => () => { stop.current = true; request.current += 1; }, []);
  const items = preview?.items || [];
  const pending = items.filter((item) => item.eligible && item.status === "pending");
  const finished = items.filter((item) => ["done", "failed"].includes(item.status)).length;
  const eligible = items.filter((item) => item.eligible).length;
  const visibleItems = items.filter((item) => `${item.productNumber || ""} ${item.serialNumbers.join(" ")} ${item.changes.join(" ")}`.toLowerCase().includes(search.toLowerCase().trim()));

  async function prepare(nextAction) {
    const id = ++request.current;
    setAnchor(null); setOpen(true); setAction(nextAction); setPreview(null); setConfirmed(false); setError(""); setSearch(""); setLoading(true);
    try {
      const input = { shopId, action: nextAction, ...(runId ? { runId, rowIds: selection.selectedIds, current: true } : { variantIds: selection.selectedIds }) };
      const { data } = await createPreview({ variables: { input } });
      if (request.current === id) setPreview(data.previewInventoryDiagnosticBatch);
    } catch (failure) { if (request.current === id) setError(failure.message); }
    finally { if (request.current === id) setLoading(false); }
  }
  function close() {
    if (executing) return;
    request.current += 1; setOpen(false); setLoading(false);
  }
  async function executePlanned() {
    if (!confirmed || !preview || !pending.length || executing) return;
    stop.current = false; setExecuting(true); setError("");
    try {
      for (const item of pending) {
        if (stop.current) break;
        const { data } = await execute({ variables: { batchId: preview._id, itemId: item.itemId } });
        const updated = data.executeInventoryDiagnosticBatchItem;
        setPreview((previous) => ({ ...previous, items: previous.items.map((entry) => entry.itemId === updated.itemId ? updated : entry) }));
        if (updated.status === "running") { setError("Ein Auftrag läuft bereits. Bitte den Status aktualisieren, bevor Sie fortfahren."); break; }
      }
    } catch (failure) {
      setError(`${failure.message} Die Ausführung wurde gestoppt. Bitte den Status aktualisieren, bevor Sie erneut Aktionen starten.`);
    } finally {
      setExecuting(false);
      selection.clear();
      if (onComplete) {
        try { await onComplete(); }
        catch (failure) { setError(`Die Liste konnte nicht aktualisiert werden: ${failure.message}`); }
      }
    }
  }
  async function refreshStatus() {
    setLoading(true);
    try {
      const { data } = await client.query({ query: batchQuery, variables: { batchId: preview._id }, fetchPolicy: "no-cache" });
      setPreview(data.inventoryDiagnosticBatch); setError("");
    } catch (failure) { setError(failure.message); }
    finally { setLoading(false); }
  }
  return <>
    <Tooltip title="Alle Zeilen der aktuellen Seite auswählen (maximal 500)">
      <span><Checkbox color="primary" checked={selection.checked} indeterminate={selection.indeterminate}
        disabled={disabled || !pageCount || executing} onChange={selection.toggleAll}
        inputProps={{ "aria-label": "Alle Zeilen dieser Seite auswählen" }} /></span>
    </Tooltip>
    <Button variant="outlined" color="primary" disabled={disabled || !selection.selectedIds.length || executing}
      onClick={(event) => setAnchor(event.currentTarget)} aria-haspopup="true" aria-controls="inventory-actions-menu" endIcon={<ChevronDownIcon />}>
      Aktionen{selection.selectedIds.length ? ` (${selection.selectedIds.length})` : ""}
    </Button>
    <Menu id="inventory-actions-menu" anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
      <MenuItem onClick={() => prepare("republish")}>Neu veröffentlichen</MenuItem>
      <MenuItem onClick={() => prepare("repair")}>Beheben</MenuItem>
    </Menu>
    <Dialog open={open} onClose={close} fullWidth maxWidth="lg" aria-labelledby="inventory-batch-title"
      disableBackdropClick={executing} disableEscapeKeyDown={executing}>
      <DialogTitle id="inventory-batch-title">{action === "repair" ? "Beheben" : "Neu veröffentlichen"} – Aktionen prüfen</DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>Prüfen Sie vor dem Bestätigen, welche Änderungen für welche Produkte geplant sind. Die Vorschau verwendet die aktuellen Backend-Daten. Eine Veröffentlichung betrifft das gesamte Produkt mit allen freigegebenen Lagerteilen.</Typography>
        {action === "republish" && <Typography paragraph color="textSecondary">„Neu veröffentlichen“ führt den Ablauf „Verstecken → Anzeigen → Veröffentlichen“ aus. Das Produkt ist danach sichtbar geschaltet; die normalen Freigaberegeln für seine Lagerteile gelten weiterhin.</Typography>}
        {action === "repair" && <Typography paragraph color="textSecondary">„Beheben“ korrigiert Bestands- und Reservierungswerte anhand des aktuellen Backend-Verkaufsstatus, offener Bestellungen und aktiver Reservierungen. Fehlende Produktgruppen werden neu aufgebaut. Der LIMES-Upload und physische Bestände werden dadurch nicht geändert.</Typography>}
        {loading && <Box display="flex" alignItems="center" p={2}><CircularProgress size={24} /><Box ml={2}>Aktuelle Aktionen werden geladen …</Box></Box>}
        {error && <Typography role="alert" color="error" paragraph>{error}</Typography>}
        {preview && <>
          <Box display="flex" flexWrap="wrap" alignItems="center" mb={2} style={{ gap: 8 }}>
            <Chip label={`${preview.selectedCount} ${preview.selectedCount === 1 ? "Zeile" : "Zeilen"} ausgewählt`} />
            <Chip color="primary" label={`${eligible} ${eligible === 1 ? "Produkt" : "Produkte"} ausführbar`} />
            <Chip label={`${items.length - eligible} ${items.length - eligible === 1 ? "Eintrag" : "Einträge"} übersprungen`} />
            {finished > 0 && <Chip label={`${finished} / ${eligible} bearbeitet`} />}
          </Box>
          <TextField fullWidth size="small" variant="outlined" label="Vorschau nach Produktnummer oder Seriennummer durchsuchen"
            value={search} onChange={(event) => setSearch(event.target.value)} />
          {executing && <Box my={2}><LinearProgress variant="determinate" value={eligible ? finished / eligible * 100 : 0} />
            <Typography variant="body2" role="status">{finished} von {eligible} Produkten bearbeitet. Bereits ausgeführte Änderungen bleiben erhalten.</Typography></Box>}
          <Box mt={2} style={{ maxHeight: "48vh", overflow: "auto" }}>
            <Table stickyHeader size="small" aria-label="Geplante Batch-Aktionen">
              <TableHead><TableRow><TableCell>Produktnummer</TableCell><TableCell>Seriennummern</TableCell><TableCell>Geplante Änderungen</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
              <TableBody>{visibleItems.map((item) => <TableRow key={item.itemId}>
                <TableCell style={{ verticalAlign: "top", minWidth: 140 }}><DiagnosticProductLink productId={item.productId} productNumber={item.productNumber} newTab /></TableCell>
                <TableCell style={{ verticalAlign: "top", maxWidth: 260, overflowWrap: "anywhere" }}>{item.serialNumbers.join(", ")}</TableCell>
                <TableCell style={{ minWidth: 320, verticalAlign: "top" }}>
                  {item.changes.map((change, index) => <Typography variant="body2" paragraph key={index}>{change}</Typography>)}
                  {item.error && <Typography variant="body2" color="error">{item.error}</Typography>}
                </TableCell>
                <TableCell style={{ verticalAlign: "top" }}><Chip size="small" label={STATUS[item.status] || item.status}
                  color={item.status === "failed" ? "secondary" : item.status === "done" ? "primary" : "default"} /></TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </Box>
          {!executing && pending.length > 0 && <FormControlLabel label="Ich habe die geplanten Änderungen geprüft."
            control={<Checkbox color="primary" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />} />}
          <Typography variant="caption" color="textSecondary">Vorschau gültig bis {new Date(preview.expiresAt).toLocaleString("de-DE")}. Zwischenzeitliche Bestandsänderungen werden vor der Ausführung erneut geprüft.</Typography>
        </>}
      </DialogContent>
      <DialogActions>
        {executing ? <Button onClick={() => { stop.current = true; }}>Nach diesem Produkt stoppen</Button> : <Button onClick={close}>{finished ? "Schließen" : "Abbrechen"}</Button>}
        {preview && !executing && <Button onClick={refreshStatus} disabled={loading}>Status aktualisieren</Button>}
        <Button color="primary" variant="contained" disableElevation onClick={executePlanned}
          disabled={loading || executing || !confirmed || !pending.length || items.some((item) => item.status === "running") || Boolean(error)}>
          {executing ? "Aktionen werden ausgeführt …" : `${pending.length} ${pending.length === 1 ? "Produkt" : "Produkte"} bestätigen und ausführen`}
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}
